import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { InputField } from '@/components/formFields/InputField';
import * as Yup from 'yup';
import AuthModal from '../registration/modals/AuthModal';
import RegistrationForm from '../registration/RegistrationForm';
import Logo from '../../images/logoMoiry.svg';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';
import { authLogin, register, googleLogin } from '@/store/actions/auth';
import { useQuery } from '../../hooks/useQuery';
import { Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';

const Login = () => {
  const [registrationVisible, setRegistrationVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const query = useQuery();
  const intended = query.get('intended');

  const registrationSubmit = (values: Record<string, any>) => {
    dispatch(register(values));
  };

  const validationSchema = Yup.object().shape({
    userName: Yup.string().required('Povinné pole'),
    password: Yup.string().required('Povinné pole'),
  });

  return (
    <div className="min-h-screen bg-neutral-50 relative overflow-hidden">
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(var(--neutral-100) 1px, transparent 1px), linear-gradient(90deg, var(--neutral-100) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-neutral-50 pointer-events-none" />

      <div className="relative z-10 grid lg:grid-cols-[1fr_1.1fr] min-h-screen">
        {/* Left: form */}
        <div className="flex flex-col px-12 py-10">
          <div className="flex items-center justify-between">
            <img alt="MOIRY" src={Logo} className="h-6" />
            <div className="text-xs text-neutral-500">
              Nemáš účet?{' '}
              <button
                type="button"
                className="text-primary-600 font-medium hover:underline"
                onClick={() => setRegistrationVisible(true)}
                data-testid="login-create-account-btn"
              >
                Vytvořit
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-sm">
              <Badge variant="brand" size="md" className="mb-5">
                Verze 2026.05
              </Badge>
              <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">Přihlášení</h1>
              <p className="text-sm text-neutral-500 mt-2">
                Vítejte zpět. Pokračujte na svou nejnovější akci.
              </p>

              <Formik
                initialValues={{ userName: '', password: '' }}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  dispatch(authLogin(values.userName, values.password, intended));
                }}
              >
                {({ errors, touched }) => (
                  <Form className="mt-8 space-y-5">
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-medium uppercase tracking-wider text-neutral-600 inline-block">
                        Uživatelské jméno nebo e-mail
                        <span className="text-danger ml-1">*</span>
                      </label>
                      <div className="relative">
                        <Mail
                          size={14}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                        />
                        <Field
                          name="userName"
                          placeholder="jmeno@spolecnost.cz"
                          component={InputField}
                          required
                          data-testid="login-username-input"
                          className="pl-9"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[11px] font-medium uppercase tracking-wider text-neutral-600 inline-block">
                        Heslo
                        <span className="text-danger ml-1">*</span>
                      </label>
                      <div className="relative">
                        <Lock
                          size={14}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                        />
                        <Field
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          placeholder="••••••••"
                          component={InputField}
                          data-testid="login-password-input"
                          className="pl-9 pr-9"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 transition-colors p-1"
                          data-testid="login-toggle-password-btn"
                        >
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" className="sr-only peer" />
                        <span className="h-4 w-4 rounded border border-neutral-300 bg-white peer-checked:bg-primary-600 peer-checked:border-primary-600 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/40 transition-colors inline-flex items-center justify-center" />
                        <span className="text-sm text-neutral-800">Zůstat přihlášen</span>
                      </label>
                      <Link
                        to="/reset-password"
                        className="text-primary-600 font-medium hover:underline"
                        data-testid="login-forgot-password-link"
                      >
                        Zapomněl jsem heslo
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      data-testid="login-submit-btn"
                    >
                      Přihlásit se
                      <ArrowRight size={14} />
                    </Button>

                    <div className="relative my-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-neutral-200" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-neutral-50 px-3 text-[11px] uppercase tracking-wider text-neutral-500">
                          nebo
                        </span>
                      </div>
                    </div>

                    <div className="w-full flex justify-center" data-testid="login-google-btn">
                      <GoogleLogin
                        onSuccess={(cred) => {
                          if (cred.credential) {
                            dispatch(googleLogin(cred.credential, intended ?? undefined));
                          }
                        }}
                        onError={() => {
                          toast.error('Chyba', {
                            description: 'Přihlášení přes Google se nezdařilo',
                          });
                        }}
                        text="continue_with"
                        width="320"
                      />
                    </div>
                  </Form>
                )}
              </Formik>

              <div className="mt-8 text-[11px] text-neutral-500 leading-relaxed">
                Přihlášením souhlasíte s{' '}
                <a data-testid="login-terms-link" className="underline cursor-pointer">
                  obchodními podmínkami
                </a>{' '}
                a{' '}
                <a data-testid="login-privacy-link" className="underline cursor-pointer">
                  zásadami ochrany osobních údajů
                </a>
                .
              </div>
            </div>
          </div>

          <div className="text-xs text-neutral-400 flex items-center justify-between">
            <span>&copy; 2026 MOIRY</span>

            {/* <div className="flex items-center gap-4">
              <a className="hover:text-neutral-700 cursor-pointer">Nápověda</a>
              <a className="hover:text-neutral-700 cursor-pointer">Status</a>
              <a className="hover:text-neutral-700 cursor-pointer">Kontakt</a>
            </div> */}
          </div>
        </div>

        {/* Right: hero / brand panel */}
        <div className="relative hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(var(--neutral-100) 1px, transparent 1px), linear-gradient(90deg, var(--neutral-100) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />

          <div className="relative z-10 flex flex-col h-full">
            <img alt="MOIRY" src={Logo} className="h-5 brightness-0 invert" />

            <div className="flex-1 flex items-center">
              <div className="max-w-lg">
                <div className="text-[11px] uppercase tracking-[0.2em] text-primary-300 font-medium mb-4">
                  Platforma pro architekty a stavaře
                </div>
                <h2 className="text-4xl font-semibold tracking-tight leading-[1.1]">
                  Jednotný standard pro <span className="text-primary-400">popis staveb</span>,
                  prvků a materiálů.
                </h2>
                <p className="text-base text-neutral-300 mt-5 leading-relaxed">
                  Spravujte třídník, skladby konstrukcí, typemarky a technické zprávy na jednom
                  místě. Připraveno pro DPS, BIM workflow a týmovou spolupráci.
                </p>

                {/* <div className="mt-10 grid grid-cols-3 gap-6">
                  {[
                    ['12 480', 'Akcí v systému'],
                    ['3.2k', 'Týmů'],
                    ['98.7%', 'Uptime'],
                  ].map(([n, l]) => (
                    <div key={l}>
                      <div className="text-2xl font-semibold tracking-tight font-mono tabular-nums">
                        {n}
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-neutral-400 mt-1">
                        {l}
                      </div>
                    </div>
                  ))}
                </div> */}
              </div>
            </div>

            {/* <div className="relative z-10 border-t border-white/10 pt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3 text-neutral-400">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span>Všechny systémy online</span>
              </div>
              <a
                data-testid="login-news-link"
                className="text-primary-300 hover:text-primary-200 font-medium inline-flex items-center gap-1.5 cursor-pointer"
              >
                Novinky 2026.05 <ArrowUpRight size={12} />
              </a>
            </div> */}
          </div>
        </div>
      </div>

      <AuthModal
        visible={registrationVisible}
        title="Nový uživatel"
        onClose={() => setRegistrationVisible(false)}
      >
        <RegistrationForm
          onSubmit={registrationSubmit}
          onClose={() => setRegistrationVisible(false)}
        />
      </AuthModal>
    </div>
  );
};

export default Login;
